/** A value that exists to be copied: a join code, a canvas address, a pairing code, an id. */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";

import { cn } from "@/lib/cn";

import { Button } from "../Button/Button";
import "./CopyField.css";

const SETTLE_MS = 1600;

export interface CopyFieldProps {
  readonly value: string;
  /** Names the value, and gives the copy control its accessible name. */
  readonly label?: string;
  /** Stand the value on its own line, for a code read off a screen. */
  readonly group?: boolean;
  readonly large?: boolean;
  readonly onCopy?: (value: string) => void;
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function CopyField({
  value,
  label,
  group = false,
  large = false,
  onCopy,
  className,
  style,
}: CopyFieldProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(value)?.catch(() => undefined);
    onCopy?.(value);
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), SETTLE_MS);
  }, [value, onCopy]);

  return (
    <div
      className={cn(
        "al-copyfield",
        group ? "al-copyfield-group" : undefined,
        large ? "al-copyfield-large" : undefined,
        className,
      )}
      style={style}
    >
      {label !== undefined ? <span className="al-copyfield-label">{label}</span> : null}
      <span className="al-copyfield-value al-mono">{value}</span>
      <Button size="sm" variant="ghost" onClick={copy} aria-label={label === undefined ? "Copy" : `Copy ${label}`}>
        {copied ? "Copied" : "Copy"}
      </Button>
      <span className="al-visually-hidden" role="status">
        {copied ? "Copied" : ""}
      </span>
    </div>
  );
}
